import React from 'react';
import classNames from 'classnames';
import { useGetConfigQuery, useGetMeQuery } from '../../store';
import { Modal } from '../../components';

const setBusinessSystemAttribute = (
    product,
    fieldName,
    fieldValueFieldName,
    fieldValue,
) => {
    const config = product?.configurators?.find((x) => {
        return x.name === 'Business System Attributes';
    });
    const field = config?.fields.find((x) => {
        return x.name === fieldName;
    });
    if (field) {
        field[fieldValueFieldName] = fieldValue;
    }
};

const setNestedConfigAttribute = (
    product,
    nestedConfigId,
    fieldName,
    fieldValueFieldName,
    fieldValue,
) => {
    const config = product?.configurators?.find((x) => {
        return x.idProduct === nestedConfigId;
    });
    const field = config?.fields.find((x) => {
        return x.name === fieldName;
    });
    if (field) {
        field[fieldValueFieldName] = fieldValue;
    }
};

const normalizeSrcAttributes = (srcAttributesArray) => {
    return srcAttributesArray.map((x) => {
        const valueFieldName = Object.keys(x)?.find((key) => {
            return !(key === 'id' || key === 'name');
        });
        return {
            name: x.name,
            valueFieldName: valueFieldName,
            value: x[valueFieldName],
        };
    });
};

const setSeismicWindAttribute = (
    product,
    nestedConfigId,
    fieldName,
    fieldValueFieldName,
    fieldValue,
) =>
    setNestedConfigAttribute(
        product,
        nestedConfigId,
        fieldName,
        fieldValueFieldName,
        fieldValue,
    );

const CpqModalInner = (props) => {
    const {
        configuratorId,
        configuredProduct,
        onSubmit,
        onClose,
        onConfiguratorLoaded,
        projectInfo,
        projectSiteInfoJson,
        viewerClassNames,
        fullScreen,
    } = props;

    const { data: config } = useGetConfigQuery();
    const { data: currentUser } = useGetMeQuery();
    const viewerElementReference = React.useRef(null);
    const [embedConfigurator, setEmbedConfigurator] = React.useState(null);
    const [configuredProductDefaults, setConfiguredProductDefaults] =
        React.useState(null);

    const currency = projectInfo?.currency ?? 'USD';

    console.log({ projectInfo });

    const kbmaxUrl = config?.kbmaxUrl;

    React.useEffect(() => {
        if (
            !viewerElementReference.current ||
            !kbmaxUrl ||
            !configuratorId ||
            !currency
        ) {
            return;
        }

        const configurator = new window.kbmax.ConfiguratorEmbed({
            kbmaxUrl,
            elementId: 'viewer',
            configuratorId: configuratorId,
            configuredProduct: configuredProduct,
            showHeader: false,
            showDrawer: false,
            showSubmitButton: true,
            showMove: false,
            loadStyle: 'none',
            idTheme: 15,
            currency,
        });

        window.__KBMaxConfig__ = configurator;

        setEmbedConfigurator(configurator);
    }, [configuratorId, currency, kbmaxUrl]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (onConfiguratorLoaded && embedConfigurator) {
            onConfiguratorLoaded(embedConfigurator);
        }
    }, [onConfiguratorLoaded, embedConfigurator]);

    React.useEffect(() => {
        if (!embedConfigurator || !configuredProductDefaults) {
            return;
        }

        embedConfigurator.setConfiguredProduct(configuredProductDefaults);
    }, [embedConfigurator, configuredProductDefaults]);

    const getCurrentConfiguredProduct = React.useCallback(() => {
        if (!embedConfigurator) {
            return null;
        }

        return new Promise((pReturnValue) => {
            embedConfigurator.getConfiguredProduct((p) => {
                pReturnValue(p);
            });
        });
    }, [embedConfigurator]);

    React.useEffect(() => {
        if (!projectInfo) {
            return;
        }

        (async () => {
            const product = await getCurrentConfiguredProduct();

            setBusinessSystemAttribute(
                product,
                'fProjectName',
                'stringValue',
                projectInfo.name,
            );
            setBusinessSystemAttribute(
                product,
                'fQuoteName',
                'stringValue',
                projectInfo.name,
            );
            setBusinessSystemAttribute(
                product,
                'fCurrency',
                'stringValue',
                projectInfo.currency,
            );
            setBusinessSystemAttribute(
                product,
                'fM3CustomerNumber',
                'stringValue',
                projectInfo.customerNumber,
            );

            if (
                projectSiteInfoJson &&
                product &&
                (product?.idProduct === config?.configurators.RoundTank ||
                    product?.idProduct ===
                        config?.configurators.IsolatedInertiaBase ||
                    product?.idProduct === config?.configurators.PipeRiser ||
                    product?.idProduct === config?.configurators.CTB ||
                    product?.idProduct === config?.configurators.ARTR ||
                    product?.idProduct ===
                        config?.configurators.IsolatorSelection ||
                    product?.idProduct === config?.configurators.LayInSolution)
            ) {
                const siteInfo = JSON.parse(projectSiteInfoJson);
                const srcAttributes = normalizeSrcAttributes(siteInfo?.fields);
                for (let idx = 0; idx < srcAttributes.length; idx++) {
                    setSeismicWindAttribute(
                        product,
                        config?.configurators.SeismicAndWind,
                        srcAttributes[idx].name,
                        srcAttributes[idx].valueFieldName,
                        srcAttributes[idx].value,
                    );
                }
            }

            return product;
        })().then(setConfiguredProductDefaults);
    }, [
        getCurrentConfiguredProduct,
        projectInfo,
        projectSiteInfoJson,
        config?.configurators.RoundTank,
        config?.configurators.IsolatedInertiaBase,
        config?.configurators.PipeRiser,
        config?.configurators.CTB,
        config?.configurators.ARTR,
        config?.configurators.IsolatorSelection,
        config?.configurators.SeismicAndWind,
        config?.configurators.LayInSolution,
    ]);

    const handleSubmit = async () => {
        const product = await getCurrentConfiguredProduct();
        onSubmit(product);
        onClose();
    };

    const viewHeightClassName = fullScreen
        ? 'h-[calc(100vh-150px)]'
        : 'h-[calc(100vh-216px)]';

    return (
        <>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div
                    className={classNames(
                        'mb-1 w-full',
                        viewHeightClassName,
                        viewerClassNames,
                    )}
                    ref={viewerElementReference}
                    id="viewer"
                />
            </div>
            <div className="px-4 py-3 sm:flex sm:px-6 justify-end">
                <button
                    onClick={onClose}
                    type="button"
                    className="swegon-like-button outlined mr-4 !px-4 amber"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSubmit}
                    type="button"
                    className="swegon-like-button contained !px-4"
                >
                    Save
                </button>
            </div>
        </>
    );
};

export const CpqModal = (props) => {
    const { data: config } = useGetConfigQuery();
    const { show, fullScreen, configuratorId, viewerClassNames, ...restProps } =
        props;
    
    React.useEffect(() => {
        if (!config?.kbmaxUrl) return;

        const addScript = document.createElement('script');
        addScript.setAttribute('src', `${config.kbmaxUrl}/embed.min.js`);
        document.body.append(addScript);

        return () => {
            return addScript.remove();
        };
    }, [show, config?.kbmaxUrl]);

    return (
        <Modal
            fullScreen={fullScreen}
            show={show}
        >
            <CpqModalInner
                viewerClassNames={viewerClassNames}
                configuratorId={configuratorId}
                fullScreen={fullScreen}
                {...restProps}
            />
        </Modal>
    );
};
